<template>
<div class="spend-months">
  <!-- The section's own name, in the small grey the two things above it are named in: the card is
       Media Spend, and this is a third statement inside it rather than a card of its own. -->
  <div v-if="label" class="clone-metric__label clone-metric__label--ask spend-months__name">
    <span>{{ label }}</span>
    <!-- What the row can be ASKED, said once beside its name rather than on every column. A chart
         whose columns open a form has to say so somewhere, and the alternative — a hint under
         thirteen bars — is the same sentence drawn thirteen times. -->
    <span v-if="hint && monthsEditable" class="spend-months__hint">{{ hint }}</span>
  </div>

  <!-- THE YEAR, MONTH BY MONTH, SPLIT BETWEEN THE MEDIA. The figure above says what is spent; this
       says when it is spent and on what, which are the two things a total cannot. Every column is
       the month's whole spend and every band inside it is one medium, in the colour that medium
       wears on its own card and in its segment of every other chart on this page — which is the only
       thing tying a card to a band.

       The columns are normalised against the tallest month, so nothing can be READ off one on its
       own: the height says the shape and the figure over it says the amount. Same bargain the
       revenue row above makes, and the same picture, so a reader who has followed one has followed
       this. -->
  <div ref="bars" class="clone-metric__bars spend-months__bars" :style="barGap">
    <component
      :is="pressable(entry) ? 'button' : 'span'"
      v-for="(entry, index) in columns"
      :key="`spend_bar_${entry.key}`"
      :type="pressable(entry) ? 'button' : null"
      class="clone-metric__bar"
      :class="{
        'clone-metric__bar--live': pressable(entry),
        'clone-metric__bar--editing': editing === index,
        'clone-metric__bar--apart': entry.apart
      }"
      :style="entry.apart ? apartStyle : null"
      :title="entry.parts ? null : entry.totalTitle"
      :aria-label="entry.totalTitle"
      @mouseenter="hovered = index"
      @mouseleave="onLeave(index)"
      @focus="hovered = index"
      @blur="onLeave(index)"
      @click="onBar(index)">
      <span v-if="showAmounts" class="clone-metric__bar-value">{{ entry.amount }}</span>
      <!-- Read from the line up, which is why the markup runs the other way round: the box is laid
           out from the top down and the media are stacked in the order the cards above them stand
           in, so the first channel is at the bottom of every column and the stack order never moves
           under the reader. -->
      <span
        v-for="segment in entry.segments"
        :key="`spend_seg_${entry.key}_${segment.key}`"
        class="clone-metric__bar-fill"
        :style="{ flexBasis: segment.basis + '%', backgroundColor: segment.color }"></span>
    </component>

    <!-- WHAT THE COLUMN IS MADE OF, NAMED AND IN ITS OWN COLOURS — AND, WHERE THE ROW MAY BE TYPED
         INTO, WHERE IT IS SAID. A native title is one grey line of arithmetic and this is a stack of
         media: each row carries the dot the band it names is drawn in, so a row of the card and a
         band of the column are one object. Written by hand rather than with v-tooltip, which lifts
         its activator out of the DOM and would be doing it to thirteen columns on every keystroke.

         ONE CARD AND NOT TWO. Hovering opens it as a reading; pressing the column PINS the same card
         and turns each figure in it into a field. That is the whole of the gesture, and it is the
         only one available here: the revenue row above is asked a month at a time because a month
         there is one amount, and a month here is the media stacked — a band three pixels tall is not
         a thing anybody can be asked to hit, and a form that opened one channel at a time would ask
         the reader to press the same column once per medium.

         Read, it lists only the media that took something — a channel switched off in a month has
         nothing to say about it. Typed, it lists them all: a month is also where a channel that buys
         nothing is given something to buy.

         Deaf to the pointer while it is a reading, so moving onto it never counts as leaving the
         column that opened it, and alive the moment it is a form. -->
    <div
      v-if="card"
      ref="card"
      class="clone-bar-tip"
      :class="{ 'clone-bar-tip--live': editing !== null }"
      :style="tipStyle"
      @focusout="onCardBlur">
      <div class="clone-bar-tip__head">{{ card.month }}</div>
      <div
        v-for="(part, position) in cardParts"
        :key="`${card.key}_${part.name}`"
        class="clone-bar-tip__row">
        <span class="clone-bar-tip__dot" :style="{ backgroundColor: part.color }"></span>
        <span class="clone-bar-tip__name">{{ part.name }}</span>
        <!-- TAB WALKS THE CARD AND THEN THE ROW. Inside the card it is the browser's own Tab and
             nothing is bound at all — the fields are in the DOM in the order they are stacked in,
             which is the order they are read in. Off the last one it becomes the revenue row's own
             gesture: what was typed is applied and the NEXT month opens, so a year is stated by
             typing rather than by pressing twelve columns with the hand off the keyboard between
             each two. Shift is the same walk backwards, off the first field to the month before. -->
        <input
          v-if="editing !== null"
          :ref="`field_${part.index}`"
          class="clone-bar-tip__field"
          :value="part.value"
          :aria-label="`${card.month} — ${part.name}`"
          @keydown.esc.exact.prevent="closeCard"
          @keydown.enter.exact.prevent="commitCard"
          @keydown.tab.exact="onTab($event, position, 1)"
          @keydown.shift.tab="onTab($event, position, -1)">
        <span v-else class="clone-bar-tip__value">{{ part.value }}</span>
      </div>
      <!-- The sum, under a rule: it is not one more medium, it is what the media above it come to.
           While the card is a form it is what the column came in at — every field is committed
           together, on the way out, so there is no half-typed month for it to be a sum of — and the
           line under it says what the three keys do rather than restating a figure that has not
           moved yet. -->
      <div class="clone-bar-tip__row clone-bar-tip__row--sum">
        <span class="clone-bar-tip__dot clone-bar-tip__dot--none"></span>
        <span class="clone-bar-tip__name">{{ card.totalLabel }}</span>
        <span class="clone-bar-tip__value">{{ card.amount }}</span>
      </div>
      <div v-if="editing !== null && keysHint" class="clone-bar-tip__keys">{{ keysHint }}</div>
    </div>
  </div>

  <!-- The x axis: one letter per month, on the same gap and the same flex, so a letter stands under
       the column it names without taking any of the height the bars are read against. The year names
       itself in full — it is not one of twelve of anything, so there is no initial to take. -->
  <div class="clone-metric__bar-axis" :class="{ 'clone-metric__bar-axis--dense': dense }" :style="barGap">
    <span
      v-for="entry in columns"
      :key="`spend_axis_${entry.key}`"
      class="clone-metric__bar-tick"
      :class="{ 'clone-metric__bar-tick--apart': entry.apart }"
      :style="entry.apart ? apartStyle : null"
      :title="entry.totalTitle">{{ entry.apart ? entry.initial : entry.initial.slice(0, 1) }}</span>
  </div>

  <!-- WHICH BAND IS WHICH MEDIUM, SAID ON THE PAGE. The card under the pointer names them, and a
       colour that can only be read by hovering is a colour the reader has to hold in their head
       between two columns — the stack is the same media in the same order in every column of every
       chart on this page, so the key belongs to the picture rather than to whatever month happens to
       be under the hand.

       In the media's own order, which is the cards' order above and the order the stack is read in
       from the line up, so the first name here is the bottom band. Every medium, whether it took
       anything this year or not: a key that dropped a channel spending nothing would be a legend
       that changes as the plan is typed. -->
  <div v-if="legend.length > 0" class="spend-months__legend">
    <span v-for="item in legend" :key="`spend_key_${item.index}`" class="spend-months__key">
      <span class="spend-months__swatch" :style="{ backgroundColor: item.color }"></span>
      <span class="spend-months__medium">{{ item.name }}</span>
    </span>
  </div>
</div>
</template>

<script>
// The media spend, month by month, split between the media. It is the row the revenue card draws
// one card above, asked about the other figure: same columns, same figures over them, same card
// under the pointer, and the components are the channels themselves rather than the four the
// decomposition names.
//
// It owns no arithmetic. Every band arrives as a percentage and a colour, every figure arrives
// formatted, and the year beside the twelve arrives as a thirteenth column of the same shape. See
// EkrmChannelCard for why that rule exists.

// How wide the card that names a month's media is. Fixed, because it stands over a column and is
// centred on it: a box whose width followed its longest channel name would move sideways as the
// pointer walked the row.
const TIP_WIDTH = 200

// And how wide it is as a FORM. A field in dollars is what each row now has to hold beside a channel
// name, and 200 is the width of a card whose values are read rather than typed into.
const FORM_WIDTH = 280

// The column the year stands in, and the air between it and December. The revenue card's own two
// numbers, so the two rows end at the same place and their rules stand on one line.
const APART_WIDTH = 96
const APART_GAP = 12

// The air between the card and the row it hangs off, on whichever side of it the card ends up. It is
// the number the two CSS rules use and the one placeCard has to ask its question against.
const TIP_GAP = 8

export default {
  name: 'EkrmSpendMonths',
  props: {
    label: { type: String, default: '' },
    // Said once beside the section's name, and only where the row can be typed into: what pressing a
    // column does. A caller that passes none gets a row that says nothing about itself.
    hint: { type: String, default: '' },
    // The keys the form answers to, spelled out at the foot of it — Tab through the stack and then
    // on to the next month, Enter to apply, Esc to drop it. Same rule as the hint: the component
    // owns no words.
    keysHint: { type: String, default: '' },
    // [{ key, initial, month, amount, totalLabel, totalTitle, segments, parts }] — `segments` are
    // percentages of the column, top-down, and `parts` are the same media named and formatted for
    // the card. A month that spends nothing still arrives, with segments that come to nothing: a
    // gap in the row is a month, not an absence.
    //
    // EVERY medium is in `parts`, whether it took anything or not: each carries `spent`, which is
    // what the reading filters on, and `index` — its own position in the caller's media list, which
    // is the only thing a typed figure comes back as.
    bars: { type: Array, required: true },
    // THE THIRTEENTH COLUMN, at its own scale: the year the twelve add up to. Same shape and same
    // colours — it is the same picture of the same media — and set apart by a rule, because a year
    // is twelve months tall and put on the months' axis it would leave them a row of hairlines.
    total: { type: Object, default: null },
    barGap: { type: Object, default: null },
    dense: { type: Boolean, default: false },
    // WHETHER A MONTH MAY BE SAID rather than only read. The year beside the twelve never is,
    // whatever this carries: it is the twelve added up, and the way to change it is to change one of
    // them — the revenue row's own rule for its own thirteenth column.
    monthsEditable: { type: Boolean, default: false }
  },
  data () {
    return {
      // The column under the pointer, or null. It names the card standing over the row and nothing
      // else: no figure moves for it.
      hovered: null,
      // The column whose card is a FORM rather than a reading, or null. One at a time, and it
      // outranks the pointer: a card pinned to July must not follow a hand crossing August on its
      // way to the field.
      editing: null,
      // Set for the one flush a commit's redraw takes. Committing moves the row, and the watcher
      // that closes a pinned card whenever the row moves is the one thing that must not close this
      // one before the click that closed it has been handled. See commitCard.
      closing: false,
      // Set while Tab is carrying the card from one month to the next. The rows are keyed by the
      // month, so the field the caret is in is taken out of the DOM and put back one column along —
      // which some browsers report as a focus LEAVING the card, and a focus leaving the card is a
      // commit that would close the month Tab has just opened. See stepCard.
      stepping: false,
      // WHERE THE CARD IS PUT, in pixels off the top of the row, and how tall it is allowed to be.
      // Both are set by placeCard and by nothing else: they are answers about the room this row
      // happens to have, not preferences. Null until it has been measured, which is the one frame
      // the card spends where the stylesheet puts it — above the row, which is where it belongs
      // whenever it fits.
      tipTop: null,
      tipMax: null
    }
  },
  computed: {
    columns () {
      return this.total ? this.bars.concat([this.total]) : this.bars
    },
    apartStyle () {
      return {
        flex: `0 0 ${APART_WIDTH}px`,
        marginLeft: `calc(${APART_GAP}px - ${(this.barGap && this.barGap.gap) || '0px'})`
      }
    },
    apartRoom () {
      return this.total ? APART_WIDTH + APART_GAP : 0
    },
    // The column the card belongs to: the one pinned, or the one under the pointer. Pinned first, so
    // a hand crossing the row on its way to a field cannot take the card off the month it opened.
    card () {
      const at = this.at
      if (at === null) return null
      const bar = this.columns[at]
      return bar && bar.parts ? bar : null
    },
    at () {
      if (this.editing !== null) return this.editing
      return this.hovered
    },
    // Read, only the media that took something; typed, all of them. See the card itself for why the
    // two lists differ.
    cardParts () {
      if (!this.card) return []
      if (this.editing !== null) return this.card.parts
      return this.card.parts.filter(part => part.spent)
    },
    // Above the chart and centred on its own column, then held between the row's two edges: a
    // January whose card hung off the left would belong to no picture at all. The revenue row's own
    // clamp. Wider as a form than as a reading, a row that has to hold a field being wider than one
    // that holds a figure.
    tipStyle () {
      const at = this.at
      if (at === null) return null
      const width = this.editing === null ? TIP_WIDTH : FORM_WIDTH
      const style = {
        width: `${width}px`,
        left: `clamp(0px, calc(${this.centreOf(at)} - ${width / 2}px), calc(100% - ${width}px))`
      }
      // And the height, once it has been measured. A stack of media is as tall as the number of
      // channels modelled, which is a figure this component cannot be written to assume, so where
      // the card goes is measured rather than declared — see placeCard.
      if (this.tipTop !== null) {
        style.top = `${this.tipTop}px`
        style.bottom = 'auto'
      }
      if (this.tipMax !== null) style.maxHeight = `${this.tipMax}px`
      return style
    },
    // The media the stack is made of, named and in their colours. Read off the columns rather than
    // taken as a prop of its own: every column already carries every medium — that is what the card
    // is drawn from — so a second list would be the same statement made twice and a chance for the
    // two to disagree. The first column that has any is enough, they all carry the same list.
    legend () {
      const named = this.columns.filter(entry => entry.parts && entry.parts.length > 0)
      return named.length > 0 ? named[0].parts : []
    },
    // Whether there is room for a figure per column. Twelve numbers fit a year; a window several
    // years wide has columns narrower than a digit, which is the same question `dense` answers.
    showAmounts () {
      return !this.dense && this.columns.every(entry => !!entry.amount)
    }
  },
  methods: {
    // The middle of one column, as a CSS length rather than a percentage: the months share whatever
    // the year's own column leaves, which is a fixed number of pixels off the row rather than a
    // share of it. The year is the last column and is measured back from the right edge.
    centreOf (index) {
      const count = this.bars.length
      if (index >= count) return `calc(100% - ${APART_WIDTH / 2}px)`
      const share = count > 0 ? (index + 0.5) / count : 0
      return `calc((100% - ${this.apartRoom}px) * ${share})`
    },
    // Guarded on the column, so the mouseleave of the one being left cannot clear a card the pointer
    // has already opened on the next.
    onLeave (index) {
      if (this.hovered === index) this.hovered = null
    },
    // Whether a column takes a press. The months do where the caller says so; the year never does.
    pressable (entry) {
      return this.monthsEditable && !entry.apart && !!entry.parts
    },
    // A column pressed. It pins the card that was already standing over it and turns its figures
    // into fields — nothing has moved yet, and pressing the column that is open closes it again, a
    // press being how it was opened.
    onBar (index) {
      const entry = this.columns[index]
      if (!entry || !this.pressable(entry)) return
      if (this.editing === index) {
        this.closeCard()
        return
      }
      this.editing = index
      this.$nextTick(() => this.focusFirst())
    },
    focusFirst () {
      this.focusPart(this.cardParts[0])
    },
    focusLast () {
      this.focusPart(this.cardParts[this.cardParts.length - 1])
    },
    focusPart (part) {
      const el = part ? this.fieldFor(part.index) : null
      if (el) {
        el.focus()
        el.select()
      }
    },
    // ONE CHANNEL'S FIELD, and the reason it is not simply `$refs[key]`. A ref inside a v-for is
    // registered as an array and Vue appends to it before it splices the departing node out, so for
    // the length of a patch the key can hold both the input that is leaving and the one that is
    // arriving — which is exactly what stepping from one month to the next does, the rows being
    // keyed by the month. The one still in the document is the one meant.
    fieldFor (index) {
      const field = this.$refs[`field_${index}`]
      const list = Array.isArray(field) ? field : [field]
      const live = list.filter(node => node && node.isConnected)
      return live.length > 0 ? live[live.length - 1] : list[list.length - 1] || null
    },
    // The card let go of without a word: everything typed into it is dropped, which is what Escape
    // has always meant and the reason the fields are not committed one at a time.
    closeCard () {
      this.editing = null
    },
    // EVERYTHING IN THE CARD AT ONCE, on the way out. A month here is the media stacked, so what is
    // being stated is the column and not one band of it: sending each field as it was left would
    // resize the year once per channel and walk the plan through levels nobody asked for.
    //
    // Only what actually changed goes up, compared as TEXT against exactly what the field was opened
    // on — the one comparison a component with no arithmetic can make — so a card opened and shut
    // again says nothing at all.
    commitCard () {
      const at = this.editing
      if (at === null) return
      const values = this.readCard(at)
      this.editing = null
      this.hovered = null
      this.sendCard(at, values)
    },
    // What the fields of one column say, against the figures they were opened on. Read off the
    // COLUMN rather than off `cardParts`, so it does not depend on which month is currently pinned:
    // stepping to the next one has to gather this one first, and a reader of the open card would be
    // gathering the month it is about to move to.
    readCard (at) {
      const bar = this.columns[at]
      const parts = bar && bar.parts ? bar.parts : []
      const values = []
      parts.forEach(part => {
        const el = this.fieldFor(part.index)
        if (!el) return
        const typed = String(el.value === null || el.value === undefined ? '' : el.value).trim()
        if (typed === '' || typed === String(part.value).trim()) return
        values.push({ index: part.index, value: typed })
      })
      return values
    },
    // A column handed up, however the card was left: on Enter, on a blur, or on the Tab that opened
    // the next month. A card opened and shut again says nothing at all.
    sendCard (at, values) {
      if (values.length === 0) return
      // Held over the flush the parent's redraw takes, so the watcher below cannot read the row
      // moving under a card that has already closed or moved itself as a reason to close it again.
      this.closing = true
      this.$nextTick(() => { this.closing = false })
      this.$emit('month', { index: at, values: values })
    },
    // TAB AT EITHER END OF THE CARD, which is where it stops being the browser's business. In the
    // middle of the stack nothing is bound: the fields are in the DOM in the order they are drawn in
    // and the default does exactly the right thing. Off the last one — or off the first one
    // backwards — it is the revenue row's own step, and December is followed by January.
    onTab (event, position, step) {
      const last = this.cardParts.length - 1
      if (step > 0 ? position < last : position > 0) return
      event.preventDefault()
      this.stepCard(step)
    },
    // THE CARD CARRIED TO THE NEXT MONTH. What was typed goes up exactly as Enter would have sent
    // it, and then the card reopens one column along — the same gesture the revenue row's own field
    // answers to, said about a card rather than about a single box.
    //
    // The card does NOT close on the way. The commit moves the row, and `bars` closes a pinned card
    // whenever the row it was opened on is replaced — which is right for a spend level moved or a
    // channel dropped, and exactly wrong here; `closing` holds it off for the one flush that redraw
    // takes. The focus is taken afterwards rather than by the browser, the fields of the month being
    // stepped to not existing until it has.
    stepCard (step) {
      const count = this.bars.length
      const at = this.editing
      if (at === null || count === 0) return
      const values = this.readCard(at)
      this.stepping = true
      this.editing = (at + step + count) % count
      this.hovered = null
      this.sendCard(at, values)
      this.$nextTick(() => {
        if (step > 0) this.focusFirst()
        else this.focusLast()
        this.stepping = false
      })
    },
    // THE CARD LEFT, however it was left: a click somewhere else on the page, or a Tab off the last
    // field. Both are the same gesture — a reader who typed and looked away must not be left with a
    // caret over a chart — and both commit, which is the bargain every other field in this wizard
    // keeps. Tab BETWEEN the fields of one card is not leaving it and says nothing: the focus is
    // still inside the box, which is what the containment test below asks.
    onCardBlur (event) {
      if (this.editing === null || this.stepping) return
      const card = this.$refs.card
      const next = event.relatedTarget
      if (card && next && card.contains(next)) return
      this.commitCard()
    },
    // WHICH SIDE OF THE ROW THE CARD STANDS ON. Over the column it names, which is where a card
    // explaining a point belongs and where every card in this wizard is drawn — unless it does not
    // fit there. This row hangs inside the wizard's own scrolling pane, and a card taller than the
    // room above the row is cut off by that pane's top edge, which takes the month's own name with
    // it: the head is the first line of the card. A stack of media grows with the number of channels
    // modelled, so how much room the card needs is not a thing this component can be written to
    // assume. It is measured.
    //
    // Measured off the ROW rather than off the card where it currently stands, so the answer does
    // not depend on the answer given last time — a card already flipped under the row has all the
    // room in the world above it, and asking about its own position would leave it there.
    placeCard () {
      if (this.at === null) {
        this.tipTop = null
        this.tipMax = null
        return
      }
      this.$nextTick(() => {
        const card = this.$refs.card
        const bars = this.$refs.bars
        if (!card || !bars) return
        const box = bars.getBoundingClientRect()
        const clip = this.clipBox(bars)
        // The card's OWN height, asked of its content rather than of its box: a cap left over from
        // the last column would otherwise be measured as the height the card wants, and a card once
        // capped would never be let out again.
        const height = card.scrollHeight
        const above = box.top - clip.top - TIP_GAP
        const below = clip.bottom - box.bottom - TIP_GAP
        if (height <= above) {
          this.tipTop = -(height + TIP_GAP)
          this.tipMax = null
        } else if (height <= below) {
          this.tipTop = box.height + TIP_GAP
          this.tipMax = null
        } else {
          // NEITHER SIDE HOLDS IT, which is what a dozen media come to. Then the card is put where
          // the whole of it can be seen — against the top of the visible pane, over the row it is a
          // reading of — rather than hung off an edge that will cut its first lines off, those being
          // the month's own name and the media under it. It is deaf to the pointer while it is a
          // reading, so the columns underneath go on being hovered through it.
          this.tipTop = clip.top + TIP_GAP - box.top
          this.tipMax = Math.max(0, clip.bottom - clip.top - 2 * TIP_GAP)
        }
      })
    },
    // WHAT WOULD DO THE CUTTING, top and bottom: every ancestor that clips its overflow, and the
    // window, narrowed one against the next. This row hangs inside the wizard's own scrolling pane
    // and may hang inside anything else on the way there, and a card is only visible where all of
    // them agree it is.
    clipBox (from) {
      let top = 0
      let bottom = window.innerHeight || document.documentElement.clientHeight
      let node = from.parentElement
      while (node) {
        const overflow = window.getComputedStyle(node).overflowY
        if (overflow !== 'visible') {
          const box = node.getBoundingClientRect()
          top = Math.max(top, box.top)
          bottom = Math.min(bottom, box.bottom)
        }
        node = node.parentElement
      }
      return { top: top, bottom: Math.max(top, bottom) }
    }
  },
  watch: {
    // The card opened, moved to another month, or turned from a reading into a form — which is the
    // one thing that changes its height without changing the column it stands on. Each is a new
    // question about whether it fits where it is being put.
    at () {
      this.placeCard()
    },
    editing () {
      this.placeCard()
    },
    // A card belongs to the row it was opened on. Committing closes it already; this is for the ways
    // the row changes that are not one — the spend level moved, a curve edited, a channel dropped —
    // which would otherwise leave a form standing over somebody else's month.
    bars () {
      if (this.closing) return
      this.editing = null
    }
  }
}
</script>

<style scoped>
  .spend-months {
    margin-top: 14px;
  }
  .spend-months__name {
    margin-bottom: 2px;
    gap: 8px;
  }
  /* What the row can be asked, beside the name of it: lighter than the name and allowed to fall off
     the end, a hint being the first thing a narrow card may give up. */
  .spend-months__hint {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 0.68rem;
    color: rgba(0, 0, 0, 0.42);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* A row of its own height rather than whatever is left of a card: this stands under two things
     that are already as tall as they need to be, so the room it takes is a statement about the row
     rather than the remainder of one. */
  .spend-months__bars {
    height: 108px;
    margin-top: 4px;
  }
  .clone-metric__bars {
    position: relative;
    display: flex;
    align-items: flex-end;
  }
  /* THE COLUMN THAT IS NOT A MONTH, set apart by a rule down its left: the same media drawn the same
     way, at its own scale, and the rule is what says the two axes are not one. */
  .clone-metric__bar--apart {
    padding-left: 12px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
  }
  .clone-metric__bar-tick--apart {
    padding-left: 12px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
  }
  /* A span where the row is only read and a button where it can be typed into, laid out by one rule:
     the browser's own button chrome is taken off so the two are the same column either way. */
  .clone-metric__bar {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    outline: none;
  }
  .clone-metric__bar--live {
    cursor: pointer;
  }
  /* The column under the pointer and the one whose card is open, lit the same way: the bands keep
     their own colours — the split is what the stack is FOR — and the column is picked out from
     behind them instead. */
  .clone-metric__bar:hover,
  .clone-metric__bar--editing {
    background-color: rgba(0, 0, 0, 0.06);
  }
  .clone-metric__bar--live:focus-visible {
    box-shadow: 0 0 0 2px rgba(67, 160, 71, 0.4);
  }
  /* Sized by flex-basis and not by height, so the figure over the stack is a flex item of the same
     column and a month at nothing still has its number standing over the axis. Neither shrinks:
     what the row states is what is drawn. */
  .clone-metric__bar-fill {
    display: block;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
  }
  /* THE MONTH'S OWN FIGURE, OVER ITS OWN COLUMN. Small and grey: it is twelve numbers at once and
     what is compared across them is the shape, which is drawn — the figures are what that shape is
     worth, read one at a time. */
  .clone-metric__bar-value {
    flex: 0 0 auto;
    padding-bottom: 2px;
    font-size: 0.55rem;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .clone-metric__bar-axis {
    display: flex;
    margin-top: 3px;
  }
  .clone-metric__bar-tick {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;
    font-size: 9px;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }
  .clone-metric__bar-axis--dense .clone-metric__bar-tick {
    font-size: 7px;
  }

  /* THE KEY, under the axis: one swatch and one name per medium, wrapping onto as many lines as the
     card's width leaves it. Small and grey — it is read once, to learn the colours, and then it is
     the columns that are looked at — and a name too long for the row is cut rather than allowed to
     push the key it belongs to onto a line of its own. */
  .spend-months__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
    margin-top: 7px;
  }
  .spend-months__key {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    max-width: 100%;
  }
  .spend-months__swatch {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }
  .spend-months__medium {
    min-width: 0;
    font-size: 0.68rem;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.55);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* The name of the section, in the card's own small grey: the same rule the two boxes above it and
     the chart beside them are named by. */
  .clone-metric__label {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__label--ask {
    letter-spacing: normal;
    text-transform: none;
    line-height: 1.3;
  }

  /* WHAT THE MONTH IS SPENT ON, in a card rather than a line: the month, each medium beside the
     colour its band is drawn in, and the figure they come to under a rule. */
  .clone-bar-tip {
    position: absolute;
    bottom: calc(100% + 8px);
    z-index: 2;
    padding: 7px 11px 8px;
    border-radius: 4px;
    color: #ffffff;
    background-color: rgba(33, 33, 33, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
    pointer-events: none;
    /* Only ever reached where the card is taller than the pane itself, and then it is the form that
       needs it: a reading is deaf to the pointer and cannot be scrolled anyway. */
    overflow-y: auto;
  }
  /* A reading is deaf to the pointer so that moving onto it never counts as leaving the column that
     opened it. A form cannot be: it is the thing being reached for. */
  .clone-bar-tip--live {
    pointer-events: auto;
  }
  .clone-bar-tip__head {
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 3px;
  }
  .clone-bar-tip__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    line-height: 1.6;
  }
  /* The sum, under a rule: it is not one more medium, it is what the media above it come to. */
  .clone-bar-tip__row--sum {
    margin-top: 3px;
    padding-top: 3px;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    font-weight: 600;
  }
  .clone-bar-tip__dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .clone-bar-tip__dot--none {
    background-color: transparent;
  }
  .clone-bar-tip__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* The figures in one column and of one width, so a stack of amounts reads as a column of amounts
     rather than as a paragraph of sentences. */
  .clone-bar-tip__value {
    flex: none;
    font-variant-numeric: tabular-nums;
  }
  /* THE MEDIUM'S OWN FIGURE, TYPED, in the row that names it. It stands exactly where the figure it
     replaces stood and is the same width as the widest of them, so the column of amounts stays a
     column while it is being written into: a field that grew with its content would move every
     other row's decimal point as it was typed. Drawn on the card's own dark ground rather than on
     white — this is one card and not a form laid over one. */
  .clone-bar-tip__field {
    flex: none;
    width: 86px;
    padding: 1px 5px;
    font-size: 0.75rem;
    line-height: 1.4;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    outline: none;
  }
  .clone-bar-tip__field:focus {
    border-color: #ffffff;
    background-color: rgba(255, 255, 255, 0.2);
  }
  /* What the two keys do, at the foot of the form and in the quietest voice the card has: it is an
     instruction and not a figure, and the figures above it are what the card is for. */
  .clone-bar-tip__keys {
    margin-top: 5px;
    font-size: 0.66rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
  }
</style>
